/**
 * Access Matrix + View-as-User (Phase 2.A.5).
 *
 * Two admin tools in one route:
 *
 *   1) MATRIX TAB — tools × workgroups grid. For each tool the cell shows
 *      whether members of that WG can see the tool (factoring restricted_
 *      to_wg_ids). Helps admins answer "who can call X?" without
 *      eyeballing JSON.
 *
 *   2) VIEW-AS TAB — admin enters a SUNet email; we fetch their WGs and
 *      show what they'd see in the catalog (tools they can call + tools
 *      blocked + reason). Helps verify the multi-user scenarios the user
 *      asked about ("3-4 users, give some access, give some all").
 */
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Eye, Info, Search, ShieldQuestion, Users } from 'lucide-react';

import type { McpTool } from '@sumcp/shared-types';

import {
  ApiError,
  useCatalogServers,
  useCatalogTools,
  usePrincipalWorkgroups,
  useWorkgroups,
} from '@/api/index.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';
import { useUrlEnum } from '@/hooks/useUrlState.js';
import { cn } from '@/lib/utils.js';

const TABS = ['Matrix', 'View as user'] as const;
type Tab = (typeof TABS)[number];

export default function AccessMatrix(): JSX.Element {
  const [tab, setTab] = useUrlEnum<Tab>('tab', TABS, 'Matrix');

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Access Matrix"
        description="Verify who can call what. Two views: the tool×WG matrix and the per-user preview."
      />

      <div className="tab-strip settings-tabs" role="tablist" aria-label="Access views">
        {TABS.map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={tab === item}
            className={cn(tab === item && 'active')}
            onClick={() => setTab(item)}
          >
            {item}
          </button>
        ))}
      </div>

      {tab === 'Matrix' && <MatrixTab />}
      {tab === 'View as user' && <ViewAsTab />}
    </>
  );
}

/* ─── Matrix tab ───────────────────────────────────────────────────── */

function MatrixTab(): JSX.Element {
  const tools = useCatalogTools();
  const servers = useCatalogServers();
  const wgs = useWorkgroups({ limit: 500 });

  const [search, setSearch] = useState('');
  const [restrictedOnly, setRestrictedOnly] = useState(false);
  // Single-WG lens — instead of N columns of WGs (doesn't scale past
  // ~10), the user picks one WG and the table shows whether each tool
  // is reachable to members of that WG. Empty = no WG lens applied
  // (just show the Restricted-to / Visible-to columns).
  const [lensWg, setLensWg] = useState<string>('');

  const allTools = useMemo(() => {
    return [
      ...(tools.data?.available ?? []),
      ...(tools.data?.requestable ?? []).map((r) => r.tool),
    ];
  }, [tools.data]);

  const serverNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of servers.data?.items ?? []) m.set(s.id, s.name);
    return m;
  }, [servers.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allTools.filter((t) => {
      if (
        q &&
        !(t.name.toLowerCase().includes(q) || (t.description ?? '').toLowerCase().includes(q))
      ) {
        return false;
      }
      if (restrictedOnly && (!t.restricted_to_wg_ids || t.restricted_to_wg_ids.length === 0)) {
        return false;
      }
      return true;
    });
  }, [allTools, search, restrictedOnly]);

  function visible(
    openToAll: boolean,
    toolRestricted: readonly string[] | undefined,
    wgId: string,
  ): 'open' | 'allow' | 'deny' | 'closed' {
    if (openToAll) return 'open';
    if (!toolRestricted || toolRestricted.length === 0) return 'closed';
    return toolRestricted.includes(wgId) ? 'allow' : 'deny';
  }

  return (
    <SectionCard
      icon={Users}
      title={`Access Matrix (${filtered.length} tools)`}
      text="One row per tool. Each tool is either open (anyone with at least one WG can call it) or restricted to a specific WG list. Pick a WG in the lens to see what they'd actually have access to."
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          marginBottom: 12,
          alignItems: 'center',
        }}
      >
        <input
          placeholder="Search tool name / description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={inputStyle}
        />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
          <input
            type="checkbox"
            checked={restrictedOnly}
            onChange={(e) => setRestrictedOnly(e.target.checked)}
          />
          Restricted tools only
        </label>
      </div>

      <fieldset
        style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 10, marginBottom: 12 }}
      >
        <legend style={{ fontSize: 13, color: 'var(--muted-foreground, #888)', padding: '0 6px' }}>
          WG access lens
        </legend>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={lensWg}
            onChange={(e) => setLensWg(e.target.value)}
            aria-label="WG access lens"
            style={{ ...inputStyle, flex: '1 1 280px', minWidth: 0 }}
          >
            <option value="">— no lens (show restriction lists only) —</option>
            {(wgs.data?.items ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.id}
                {w.display_name ? ` — ${w.display_name}` : ''}
              </option>
            ))}
          </select>
          {lensWg && (
            <button
              type="button"
              onClick={() => setLensWg('')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid var(--line-strong)',
                background: 'rgba(255,255,255,0.85)',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Clear lens
            </button>
          )}
          <span className="subtle" style={{ fontSize: 12 }}>
            Pick one WG to see whether each tool is reachable to its members. Scales to any number
            of WGs because there's only ever one column of "allow / deny" — not N.
          </span>
        </div>
      </fieldset>

      <p
        className="subtle"
        style={{
          fontSize: 12,
          margin: '0 0 12px',
          padding: '8px 12px',
          background: 'rgba(70, 100, 140, 0.06)',
          border: '1px solid var(--line)',
          borderRadius: 6,
        }}
      >
        Read-only audit view. To grant a WG access to specific tools or a whole server, open{' '}
        <a href="/admin/workgroups">Admin → Workgroups</a> → click the WG → use the{' '}
        <strong>Access grants</strong> tab.
      </p>
      <AsyncShell
        isLoading={tools.isLoading || wgs.isLoading}
        isError={tools.isError || wgs.isError}
        isEmpty={filtered.length === 0}
        error={tools.error ?? wgs.error}
        loadingTitle="Loading tools + workgroups"
        errorTitle="Catalog API unreachable"
        emptyTitle={search || restrictedOnly ? 'No matches' : 'No tools yet'}
      >
        <div style={{ overflow: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
          <table className="data-table" style={{ fontSize: 13, minWidth: 600 }}>
            <thead>
              <tr>
                <th>Tool</th>
                <th>Server</th>
                <th>Class</th>
                <th style={{ textAlign: 'center' }}>Visible to</th>
                {lensWg && (
                  <th style={{ textAlign: 'center', minWidth: 120 }}>
                    <code style={{ fontSize: 11 }}>{lensWg}</code>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const restrictions = (t.restricted_to_wg_ids ?? []) as string[];
                const openToAll = (t as { open_to_all_wgs?: boolean }).open_to_all_wgs === true;
                const v = visible(openToAll, restrictions, lensWg);
                return (
                  <tr key={t.id}>
                    <td>
                      <code>{t.name}</code>
                    </td>
                    <td>{serverNameById.get(t.server_id) ?? t.server_id.slice(0, 8) + '…'}</td>
                    <td>
                      <span className="subtle">{t.data_classification}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <VisibleToCell openToAll={openToAll} wgIds={restrictions} toolName={t.name} />
                    </td>
                    {lensWg && (
                      <td style={{ textAlign: 'center' }}>
                        <span
                          title={
                            v === 'open'
                              ? 'open to all Stanford WGs'
                              : v === 'closed'
                                ? 'closed — nobody can call this tool'
                                : v === 'allow'
                                  ? `${lensWg} is in the restricted list`
                                  : `${lensWg} is NOT in the restricted list`
                          }
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 999,
                            fontSize: 11,
                            background:
                              v === 'open'
                                ? 'rgba(95,178,122,0.20)'
                                : v === 'closed'
                                  ? 'rgba(0,0,0,0.10)'
                                  : v === 'allow'
                                    ? 'rgba(95,178,122,0.20)'
                                    : 'rgba(217,119,102,0.18)',
                            color:
                              v === 'deny' ? '#c0392b' : v === 'closed' ? '#6b7280' : 'inherit',
                          }}
                        >
                          {v === 'open'
                            ? '✓ open to all'
                            : v === 'closed'
                              ? '🛑 closed'
                              : v === 'allow'
                                ? '✓ allow'
                                : '✗ deny'}
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </AsyncShell>
    </SectionCard>
  );
}

/**
 * "Visible to" cell. Shows `all WGs` (open) or a clickable `N WGs ⓘ`
 * pill that opens a centered modal dialog listing the full WG set.
 * Modal (not inline popover) so it has room for pagination / search /
 * filter tomorrow without being clipped by table overflow.
 */
function VisibleToCell({
  openToAll,
  wgIds,
  toolName,
}: {
  openToAll: boolean;
  wgIds: readonly string[];
  toolName?: string;
}): JSX.Element {
  const [showModal, setShowModal] = useState(false);

  if (openToAll) {
    return (
      <span
        title="Open to all — any Stanford WG member can call this tool."
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: 11,
          background: 'rgba(95,178,122,0.20)',
          color: '#1f5a37',
          fontWeight: 600,
          border: '1px solid rgba(95,178,122,0.45)',
        }}
      >
        ✓ all WGs
      </span>
    );
  }
  if (wgIds.length === 0) {
    return (
      <span
        title="Closed — publisher hasn't granted access yet. Nobody can call this tool."
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: 11,
          background: 'rgba(0,0,0,0.08)',
          color: '#6b7280',
          fontWeight: 600,
          border: '1px dashed var(--line-strong)',
        }}
      >
        🛑 closed
      </span>
    );
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        title="Click to see the full WG list"
        style={{
          padding: '2px 8px',
          borderRadius: 999,
          fontSize: 11,
          background: 'rgba(95,178,122,0.20)',
          color: '#1f5a37',
          fontWeight: 600,
          border: '1px solid rgba(95,178,122,0.45)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <span>
          {wgIds.length} WG{wgIds.length === 1 ? '' : 's'}
        </span>
        <Info size={11} aria-hidden />
      </button>
      {showModal && (
        <WgListModal wgIds={wgIds} toolName={toolName} onClose={() => setShowModal(false)} />
      )}
    </>
  );
}

/**
 * Centered modal for the "Visible to" WG list. Renders an overlay +
 * dialog with click-outside + Esc dismiss. Plenty of room for the
 * pagination / search filters we want to add next.
 */
function WgListModal({
  wgIds,
  toolName,
  onClose,
}: {
  wgIds: readonly string[];
  toolName?: string;
  onClose: () => void;
}): JSX.Element {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while the dialog is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const filtered = filter.trim()
    ? wgIds.filter((id) => id.toLowerCase().includes(filter.trim().toLowerCase()))
    : wgIds;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Workgroup list"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        ref={dialogRef}
        style={{
          background: 'var(--background, white)',
          color: 'inherit',
          border: '1px solid var(--line-strong)',
          borderRadius: 10,
          boxShadow: '0 20px 50px rgba(0,0,0,0.35)',
          width: 'min(520px, 100%)',
          maxHeight: 'min(70vh, 640px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid var(--line)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>
              Workgroups with access {toolName ? '— ' : ''}
              {toolName && <code style={{ fontSize: 13, fontWeight: 700 }}>{toolName}</code>}
            </div>
            <div className="subtle" style={{ fontSize: 12 }}>
              Members of any one of these {wgIds.length} workgroup{wgIds.length === 1 ? '' : 's'}{' '}
              can call this tool.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              border: '1px solid var(--line)',
              background: 'transparent',
              borderRadius: 6,
              padding: '4px 10px',
              fontSize: 13,
              cursor: 'pointer',
              flexShrink: 0,
              marginLeft: 12,
            }}
          >
            Close
          </button>
        </div>

        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--line)' }}>
          <input
            type="search"
            placeholder="Filter workgroups…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            autoFocus
            style={{
              width: '100%',
              padding: '6px 10px',
              border: '1px solid var(--line)',
              borderRadius: 6,
              fontSize: 13,
              background: 'transparent',
              color: 'inherit',
            }}
          />
        </div>

        <div style={{ overflow: 'auto', padding: '6px 8px' }}>
          {filtered.length === 0 ? (
            <p
              className="subtle"
              style={{ fontSize: 13, margin: 0, padding: '12px 8px', textAlign: 'center' }}
            >
              No workgroups match “{filter}”.
            </p>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {filtered.map((id) => (
                <li
                  key={id}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    fontSize: 13,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <code style={{ fontSize: 12 }}>{id}</code>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          style={{
            padding: '10px 16px',
            borderTop: '1px solid var(--line)',
            fontSize: 12,
            color: 'var(--muted-foreground, #6b7280)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span>
            Showing {filtered.length} of {wgIds.length}
          </span>
          <span className="subtle">
            Edit grants in <a href="/admin/workgroups">Admin → Workgroups</a>.
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── View-as-User tab ─────────────────────────────────────────────── */

function ViewAsTab(): JSX.Element {
  const [sub, setSub] = useState('');
  const [committed, setCommitted] = useState('');
  const [showSub, setShowSub] = useState<string | undefined>(undefined);
  const [inputError, setInputError] = useState<string | null>(null);

  const userWgs = usePrincipalWorkgroups(showSub);
  // Server-side view-as: when `showSub` is set the catalog endpoint
  // partitions tools as if `showSub` were the caller. This is the only
  // correct way — pure client-side filtering of the admin's view drops
  // tools the admin can't even request access to and produces 0-visible
  // artifacts for many admin/user pairs.
  const asUserCatalog = useCatalogTools({ asPrincipal: showSub });
  const apiError =
    userWgs.error instanceof ApiError
      ? userWgs.error.status === 400
        ? `${committed} isn't a valid email — ${(userWgs.error.body as { error?: string })?.error ?? 'invalid_principal_sub'}.`
        : `Lookup failed (${userWgs.error.status}).`
      : userWgs.error
        ? (userWgs.error as Error).message
        : asUserCatalog.error instanceof ApiError
          ? `Catalog preview failed (${asUserCatalog.error.status}): ${JSON.stringify(asUserCatalog.error.body)}`
          : asUserCatalog.error
            ? (asUserCatalog.error as Error).message
            : null;
  const servers = useCatalogServers();

  const serverNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of servers.data?.items ?? []) m.set(s.id, s.name);
    return m;
  }, [servers.data]);

  // Use the server's partition directly — no client-side recompute.
  const partition = useMemo(() => {
    const data = asUserCatalog.data;
    if (!data)
      return { visible: [] as McpTool[], blocked: [] as { tool: McpTool; missing: string[] }[] };
    const visible: McpTool[] = [...(data.available ?? [])];
    const blocked: { tool: McpTool; missing: string[] }[] = [];
    for (const r of data.requestable ?? []) {
      blocked.push({ tool: r.tool, missing: r.missing_wg ? [r.missing_wg] : [] });
    }
    for (const t of data.hidden ?? []) {
      // Closed-by-default or restricted to non-requestable WGs. We surface
      // them to the admin so they understand the full picture; "missing"
      // is empty because there's no WG the user could be added to that
      // unlocks it.
      blocked.push({ tool: t, missing: [] });
    }
    return { visible, blocked };
  }, [asUserCatalog.data]);

  function onLoad(): void {
    const next = sub.trim();
    if (!next) return;
    // Match the server-side StanfordPrincipalSubSchema (email format).
    // Cheap regex — the real authoritative check is server-side.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next)) {
      setInputError('Enter a full email address (e.g. alice@stanford.edu).');
      return;
    }
    setInputError(null);
    setCommitted(next);
    setShowSub(next);
  }

  return (
    <SectionCard
      icon={Eye}
      title="View as user"
      text="Enter a user's email. We fetch their workgroups and show what they would see in the catalog."
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          type="email"
          placeholder="alice@stanford.edu"
          value={sub}
          onChange={(e) => {
            setSub(e.target.value);
            if (inputError) setInputError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onLoad();
          }}
          style={{ ...inputStyle, flex: 1 }}
          aria-invalid={inputError ? 'true' : 'false'}
        />
        <ActionButton icon={Search} primary onClick={onLoad}>
          Load
        </ActionButton>
      </div>
      {inputError && (
        <p
          role="alert"
          style={{
            margin: '0 0 12px',
            padding: '8px 12px',
            borderRadius: 8,
            background: '#fff1f0',
            border: '1px solid #e8b3b3',
            color: '#7a1f1f',
            fontSize: 13,
          }}
        >
          {inputError}
        </p>
      )}
      {apiError && (
        <p
          role="alert"
          style={{
            margin: '0 0 12px',
            padding: '8px 12px',
            borderRadius: 8,
            background: '#fff1f0',
            border: '1px solid #e8b3b3',
            color: '#7a1f1f',
            fontSize: 13,
          }}
        >
          {apiError}
        </p>
      )}

      {showSub && (
        <>
          <div
            style={{
              background: 'rgba(0,0,0,0.05)',
              padding: 10,
              borderRadius: 8,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            <strong>Showing what </strong>
            <code>{committed}</code>
            <strong> would see</strong>
            <br />
            <span className="subtle">Workgroups:</span>{' '}
            {userWgs.isLoading ? (
              '…'
            ) : userWgs.data && userWgs.data.items.length > 0 ? (
              userWgs.data.items.map((w) => (
                <code key={w.id} style={{ marginRight: 6, fontSize: 11 }}>
                  {w.id}
                </code>
              ))
            ) : (
              <span style={{ color: '#c0392b' }}>
                (none — they would see only unrestricted tools)
              </span>
            )}
          </div>

          <SectionCard
            icon={ShieldQuestion}
            title={`Visible (${partition.visible.length})`}
            text="Tools this user can call right now."
          >
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
              {partition.visible.length === 0 && <li className="subtle">No tools.</li>}
              {partition.visible.map((t) => (
                <li key={t.id}>
                  <code>{t.name}</code>{' '}
                  <span className="subtle" style={{ fontSize: 12 }}>
                    · {serverNameById.get(t.server_id) ?? t.server_id.slice(0, 8) + '…'} ·{' '}
                    {t.data_classification}
                  </span>
                </li>
              ))}
            </ul>
          </SectionCard>

          <SectionCard
            icon={ShieldQuestion}
            title={`Blocked by restriction (${partition.blocked.length})`}
            text="Tools this user can't call because they're missing the required WG. Add them to one of the listed WGs to grant access."
          >
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 14, lineHeight: 1.6 }}>
              {partition.blocked.length === 0 && <li className="subtle">No blocked tools.</li>}
              {partition.blocked.map(({ tool, missing }) => (
                <li key={tool.id}>
                  <code>{tool.name}</code>{' '}
                  <span className="subtle" style={{ fontSize: 12 }}>
                    · needs one of:{' '}
                    {missing.map((m) => (
                      <code key={m} style={{ marginRight: 4 }}>
                        {m}
                      </code>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </SectionCard>
        </>
      )}
    </SectionCard>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px',
  border: '1px solid var(--line)',
  borderRadius: 8,
  fontSize: 14,
  background: 'transparent',
  color: 'inherit',
  flex: '1 1 240px',
  minWidth: 0,
  boxSizing: 'border-box',
};
